import { Table } from 'antd'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import getWalletReport, {
	GetWalletReportRecord,
	GetWalletReportResponse,
} from 'api/walletReport/getWalletReport'
import React, { useEffect, useMemo, useState } from 'react'
import handleFormError from 'utils/handleFormError'
import { PaginationState } from 'types'
import styles from './styles.module.scss'

interface PropType {
	setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
}

const WalletReportTable: React.FC<PropType> = ({ setPagination }) => {
	const [records, setRecords] = useState<GetWalletReportResponse['records']>([])
	const [loading, setLoading] = useState<boolean>(false)

	const columns: ColumnsType<GetWalletReportRecord> = [
		{
			title: 'Id',
			dataIndex: '_id',
			key: '_id',
		},
		{
			title: 'Client',
			dataIndex: 'organizationName',
			key: 'organizationName',
		},
		{
			title: 'Wallet Reserver',
			dataIndex: 'wallet',
			key: 'wallet_reverser',
			render: (wallet: GetWalletReportRecord['wallet']) => {
				if (wallet.amount.reserve < 0) {
					return (
						<span className={styles.negative}>
							₹ {Number(wallet.amount.reserve).toPrecision(4)}
						</span>
					)
				}
				if (wallet.amount.reserve > 0) {
					return (
						<span className={styles.positive}>
							₹ {Number(wallet.amount.reserve).toPrecision(4)}
						</span>
					)
				}
				return `₹ ${Number(wallet.amount.reserve).toPrecision(4)}`
			},
		},
		{
			title: 'Wallet Balance',
			dataIndex: 'wallet',
			key: 'wallet_balance',

			render: (wallet: GetWalletReportRecord['wallet']) => {
				return `₹ ${wallet.amount.credit.toFixed(2)}`
			},
		},
	]
	const [currentPage, setCurrentPage] = useState(1)
	const [totalCount, setTotalCount] = useState(0)
	const [pageSize, setPageSize] = useState(10)

	const pagination = useMemo(() => {
		return { current: currentPage, total: totalCount, pageSize }
	}, [currentPage, totalCount, pageSize])

	const getWalletReportAction = async (page: number, size: number) => {
		setLoading(true)
		try {
			const response = await getWalletReport({ page, size })
			setPagination(prev => {
				return {
					...prev,
					currentPage: response.currentPage,
					pageSize,
				}
			})
			setCurrentPage(response.currentPage)
			setTotalCount(response.totalCount)
			setRecords(response.records)
		} catch (error: any) {
			handleFormError(error)
		} finally {
			setLoading(false)
		}
	}

	const handlePagination = async (changes: TablePaginationConfig) => {
		setCurrentPage(changes.current ? changes.current : 1)
		setPageSize(prev => {
			if (prev !== changes.pageSize) {
				setCurrentPage(1)
			}
			return changes.pageSize ? changes.pageSize : 10
		})
		setPagination({
			currentPage,
			totalCount,
			pageSize,
		})
	}

	useEffect(() => {
		getWalletReportAction(currentPage, pageSize)
	}, [currentPage, pageSize])

	return (
		<Table
			className={styles.wallet}
			columns={columns}
			dataSource={records}
			pagination={{ ...pagination, position: ['bottomCenter'] }}
			loading={loading}
			rowKey='_id'
			onChange={handlePagination}
		/>
	)
}

export default WalletReportTable
